import type { CSSProperties } from 'react';
import { defaultThemeTokens } from '@course-authoring/shared-types';
import { adminButton, adminFocusable, adminText, adminTokens as t } from '../../admin-ui';
import { createCenterstageStyles, type CenterstageStyles } from '../centerstage/centerstageStyles';

/**
 * The Author with AI video picker and its preview: the Centerstage page's
 * search, chip, grid and video dialog styles at modal scale (Sara's in-flow
 * picker, 2026-11-01), plus the picker's own header, cards and preview buttons.
 */

const MODAL_PAD = '22px';
const CARD_FADE = '120ms ease';

/** `text.primary` at the given strength, for the still's scrim and the Select shadow. */
const ink = (percent: number) => `color-mix(in srgb, ${t.text.primary} ${percent}%, transparent)`;

export type VideoPickerStyleKey =
  | 'header'
  | 'headerText'
  | 'title'
  | 'description'
  | 'close'
  | 'find'
  | 'body'
  | 'pickCard'
  | 'pickCardActive'
  | 'pickCardButton'
  | 'pickCardBody'
  | 'runtime'
  | 'thumbScrim'
  | 'playGlyph'
  | 'selectPill'
  | 'selectPillHover'
  | 'cardError'
  | 'secondaryButton'
  | 'primaryButton'
  | 'buttonDisabled';

export interface VideoPickerStyles {
  /** The shared Centerstage pieces (search pill, chip, grid, panels). */
  centerstage: CenterstageStyles;
  picker: Record<VideoPickerStyleKey, CSSProperties>;
}

export function createVideoPickerStyles({ narrow, reducedMotion = false }: { narrow: boolean; reducedMotion?: boolean }): VideoPickerStyles {
  const base = createCenterstageStyles({ narrow });
  const fade = (...properties: string[]) => (reducedMotion ? 'none' : properties.map((property) => `${property} ${CARD_FADE}`).join(', '));
  const centerstage: CenterstageStyles = {
    ...base,
    grid: { ...base.grid, gridTemplateColumns: narrow ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '16px' },
    findBar: { ...base.findBar, flexWrap: 'nowrap' },
    searchRow: { ...base.searchRow, flex: '1 1 auto' },
  };

  return {
    centerstage,
    picker: {
      header: {
        flex: 'none',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        padding: `${MODAL_PAD} ${MODAL_PAD} 18px`,
        borderBottom: `1px solid ${t.border.subtle}`,
        fontFamily: t.typography.fontFamily,
      },
      headerText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
      title: { ...adminText.pageTitle, fontSize: '22px' },
      description: adminText.secondary,
      close: { ...adminButton.icon, flexShrink: 0 },
      find: {
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: `16px ${MODAL_PAD}`,
        borderBottom: `1px solid ${t.border.subtle}`,
        fontFamily: t.typography.fontFamily,
      },
      body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: `18px ${MODAL_PAD} ${MODAL_PAD}`,
        backgroundColor: t.surface.canvas,
        fontFamily: t.typography.fontFamily,
      },

      // Card: a container holding two sibling buttons (open the preview, Select).
      pickCard: {
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: t.surface.panel,
        border: `1px solid ${t.border.subtle}`,
        borderRadius: t.radius.tile,
        boxShadow: t.shadow.panel,
        overflow: 'hidden',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      },
      pickCardActive: base.cardHover,
      pickCardButton: {
        ...adminFocusable,
        // The card clips its corners, so the ring sits inside the edge.
        outlineOffset: '-3px',
        flex: 1,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        border: 'none',
        background: 'none',
        textAlign: 'left',
        fontFamily: 'inherit',
        color: t.text.primary,
        cursor: 'pointer',
      },
      pickCardBody: { display: 'flex', flexDirection: 'column', gap: t.spacing.xs, padding: '11px 12px 12px' },
      runtime: {
        fontSize: t.typography.caption.size,
        lineHeight: t.typography.caption.lineHeight,
        color: t.text.secondary,
        fontVariantNumeric: 'tabular-nums',
      },
      // Card hover (Sara, 2026-11-01): the scrim, play button and Select show only on the card
      // being pointed at or focused, so the stills stay clear while scanning. The scrim gives both
      // controls one ground whatever the still is. It sits above the still and below the controls.
      thumbScrim: {
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        backgroundColor: ink(34),
        // Clicks go through to the card.
        pointerEvents: 'none',
        transition: fade('opacity'),
      },
      // No hover state of its own: the whole card is the preview button.
      playGlyph: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        zIndex: 2,
        transform: 'translate(-50%, -50%)',
        width: '52px',
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: t.radius.pill,
        backgroundColor: t.surface.panel,
        // The course UI blue (#5B8EFF), outside account theming.
        color: defaultThemeTokens.ui.fixedAccent,
        fontSize: '19px',
        transition: fade('opacity'),
      },
      selectPill: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 3,
        padding: '7px 16px',
        border: 'none',
        borderRadius: t.radius.pill,
        backgroundColor: t.surface.panel,
        color: t.text.primary,
        boxShadow: `0 2px 8px ${ink(35)}`,
        fontFamily: t.typography.fontFamily,
        fontSize: t.typography.label.size,
        fontWeight: 600,
        cursor: 'pointer',
        // White, not the admin blue: the ring sits on the scrimmed still and next to the magenta fill.
        outlineColor: t.surface.panel,
        outlineWidth: '2px',
        outlineOffset: '2px',
        transition: fade('opacity', 'background-color', 'color'),
      },
      // Only while the pointer is on Select itself, not anywhere on the card.
      selectPillHover: { backgroundColor: t.brand.roseForeground, color: t.text.onPrimary },
      cardError: {
        margin: 0,
        padding: '0 12px 12px',
        fontSize: t.typography.caption.size,
        lineHeight: t.typography.caption.lineHeight,
        color: t.status.error.text,
      },

      // Preview footer (the dialog itself is the Centerstage page's)
      secondaryButton: adminButton.secondary,
      primaryButton: adminButton.primary,
      buttonDisabled: adminButton.disabled,
    },
  };
}
